/**
 * The interview question bank.
 *
 * The one rule this screen is built around: you answer before you see the answer.
 * That is enforced by the API — the question list arrives with no answers in it at
 * all, and the reveal endpoint refuses until an attempt has been saved — so this UI
 * is not the security boundary, just the experience.
 *
 * Hints sit on a ladder rather than behind a single button. Level 1 gives the SHAPE
 * of a strong answer, not its content, because telling someone "cover purpose,
 * controls, exception path and verification" helps them think, while showing the
 * first keyword just gives it away.
 */
import { useMemo, useState } from 'react';
import { CaretLeft, Eye, FloppyDisk, Lightbulb, Question as QuestionIcon, Sparkle, User } from '@phosphor-icons/react';
import type { BankQuestionPublic, BankReveal } from '@cvs/shared';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Button, Card, EmptyState, ErrorState, LoadingState, Pill, cx } from './ui';

const TOPIC_LABEL: Record<string, string> = {
  'entra-iam': 'Entra ID & identity',
  sentinel: 'Microsoft Sentinel',
  'incident-response': 'Incident response',
  general: 'General & behavioural',
};

const SUBTOPIC_LABEL: Record<string, string> = {
  'conditional-access': 'Conditional Access',
  'hybrid-sync': 'Hybrid identity & sync',
  'mfa-auth-methods': 'MFA & auth methods',
  'pim-privileged': 'PIM & privileged access',
  'app-sso-scim': 'App integration & SSO',
  'roles-lifecycle': 'Roles & lifecycle',
};

/** How an answer is labelled when revealed. An AI draft must never wear a practitioner's face. */
const SOURCE_PRESENTATION: Record<string, { label: string; tone: string; icon: typeof User }> = {
  practitioner: {
    label: 'How a practitioner actually answered this',
    tone: 'border-brand-teal/40 bg-brand-teal/5',
    icon: User,
  },
  'ai-draft': {
    label: 'Suggested structure — AI draft, reviewed by your instructor',
    tone: 'border-slate-200 bg-slate-50',
    icon: Sparkle,
  },
  'instructor-edited': {
    label: 'Written by your instructor',
    tone: 'border-brand-teal/40 bg-brand-teal/5',
    icon: User,
  },
  none: { label: 'No model answer — self-assess against the key points', tone: 'border-slate-200 bg-slate-50', icon: QuestionIcon },
};

export function QuestionBankPanel() {
  const state = useAsync(() => api.bank(), []);
  const [topic, setTopic] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [attempted, setAttempted] = useState<Set<string>>(new Set());

  const grouped = useMemo(() => {
    const byTopic = new Map<string, BankQuestionPublic[]>();
    for (const q of state.data?.questions ?? []) {
      if (!byTopic.has(q.topic)) byTopic.set(q.topic, []);
      byTopic.get(q.topic)!.push(q);
    }
    return byTopic;
  }, [state.data]);

  if (state.loading) return <LoadingState />;
  if (state.error) return <ErrorState message="The question bank could not be loaded." onRetry={state.reload} />;

  const questions = state.data?.questions ?? [];
  if (questions.length === 0) {
    return (
      <EmptyState
        icon={QuestionIcon}
        title="No bank questions yet"
        description="Interview questions appear here once your instructor publishes them for this course."
      />
    );
  }

  const done = new Set([...(state.data?.attemptedIds ?? []), ...attempted]);

  // ── one question, open ────────────────────────────────────────────────────
  if (openId) {
    const q = questions.find((x) => x.id === openId);
    if (q) {
      return (
        <BankQuestion
          question={q}
          gradingEnabled={!!state.data?.gradingEnabled}
          alreadyAttempted={done.has(q.id)}
          onAttempted={() => setAttempted((s) => new Set(s).add(q.id))}
          onBack={() => setOpenId(null)}
        />
      );
    }
  }

  // ── a topic's questions ───────────────────────────────────────────────────
  if (topic) {
    const list = grouped.get(topic) ?? [];
    return (
      <div className="space-y-4">
        <button type="button" onClick={() => setTopic(null)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-teal hover:underline">
          <CaretLeft size={15} weight="bold" /> All topics
        </button>
        <h3 className="font-display text-xl font-bold text-brand-navy">{TOPIC_LABEL[topic] ?? topic}</h3>
        <div className="space-y-2">
          {list.map((q) => (
            <button
              key={q.id}
              type="button"
              onClick={() => setOpenId(q.id)}
              className="flex w-full items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:border-brand-teal/50 hover:shadow-sm"
            >
              <span className={cx('mt-1 h-2 w-2 shrink-0 rounded-full', done.has(q.id) ? 'bg-brand-teal' : 'bg-slate-300')} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-slate-700">{q.question}</span>
                <span className="mt-1 flex flex-wrap items-center gap-2">
                  {q.subTopic && <Pill tone="slate">{SUBTOPIC_LABEL[q.subTopic] ?? q.subTopic}</Pill>}
                  {q.kind === 'scenario' && <Pill tone="amber">scenario</Pill>}
                  {done.has(q.id) && <span className="text-xs font-medium text-brand-teal">answered</span>}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── topic list ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Real questions from real interviews. Write your answer first — the model answer unlocks once you have saved
        an attempt, which is the whole point: the gap between what you wrote and what a practitioner said is the lesson.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {[...grouped.entries()].map(([t, list]) => {
          const answered = list.filter((q) => done.has(q.id)).length;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTopic(t)}
              className="rounded-2xl border border-slate-200 bg-white p-5 text-left transition hover:border-brand-teal/50 hover:shadow-card"
            >
              <p className="font-display text-lg font-bold text-brand-navy">{TOPIC_LABEL[t] ?? t}</p>
              <p className="mt-1 text-sm text-slate-500">
                {list.length} question{list.length === 1 ? '' : 's'} · {answered} answered
              </p>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-brand-teal transition-all" style={{ width: `${(answered / list.length) * 100}%` }} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── a single question ────────────────────────────────────────────────────────

function BankQuestion({
  question,
  gradingEnabled,
  alreadyAttempted,
  onAttempted,
  onBack,
}: {
  question: BankQuestionPublic;
  gradingEnabled: boolean;
  alreadyAttempted: boolean;
  onAttempted: () => void;
  onBack: () => void;
}) {
  const [answer, setAnswer] = useState('');
  const [hintLevel, setHintLevel] = useState(0);
  const [hints, setHints] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(alreadyAttempted);
  const [reveal, setReveal] = useState<BankReveal | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function takeHint() {
    const next = hintLevel + 1;
    try {
      const r = await api.bankHint(question.id, next);
      if (r.hint) {
        setHints((h) => [...h, r.hint!]);
        setHintLevel(next);
      }
    } catch {
      setError('That hint could not be loaded.');
    }
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.saveBankAnswer(question.id, { answer, hintLevel, mode: 'drill' });
      setSaved(true);
      onAttempted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Your answer could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  async function showAnswer() {
    setBusy(true);
    setError(null);
    try {
      setReveal(await api.revealBankAnswer(question.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The answer could not be loaded.');
    } finally {
      setBusy(false);
    }
  }

  const presentation = SOURCE_PRESENTATION[reveal?.answerSource ?? 'none'] ?? SOURCE_PRESENTATION.none;
  const SourceIcon = presentation.icon;

  return (
    <div className="space-y-4">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-teal hover:underline">
        <CaretLeft size={15} weight="bold" /> Back
      </button>

      <Card className="p-6">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {question.subTopic && <Pill tone="slate">{SUBTOPIC_LABEL[question.subTopic] ?? question.subTopic}</Pill>}
          {question.kind === 'scenario' && <Pill tone="amber">scenario</Pill>}
          {!question.hasAnswer && <Pill tone="slate">no model answer — self-assess</Pill>}
        </div>
        <p className="font-display text-lg font-semibold leading-snug text-brand-navy">{question.question}</p>

        <textarea
          className="mt-4 w-full resize-y rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20"
          rows={7}
          placeholder="Answer as you would out loud in the interview…"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
        />

        {hints.length > 0 && (
          <div className="mt-3 space-y-2">
            {hints.map((h, i) => (
              <div key={i} className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                <p className="label-mono mb-1 text-amber-700">Hint {i + 1}</p>
                <p className="text-sm text-amber-900">{h}</p>
              </div>
            ))}
          </div>
        )}

        {error && <p className="mt-3 text-sm font-medium text-rose-600">{error}</p>}

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button icon={FloppyDisk} onClick={save} loading={saving} disabled={!answer.trim() || saving}>
            {saved ? 'Save again' : 'Save my answer'}
          </Button>

          {hintLevel < question.hintCount && (
            <Button variant="secondary" icon={Lightbulb} onClick={takeHint}>
              {hintLevel === 0 ? 'I need a hint' : 'Another hint'}
            </Button>
          )}

          {/* The reveal only appears once an attempt exists. The server enforces this
              too — this is convenience, not the control. */}
          {saved && !reveal && (
            <Button variant="secondary" icon={Eye} onClick={showAnswer} loading={busy}>
              Show me how it was answered
            </Button>
          )}

          {!saved && question.keyPointCount > 0 && (
            <span className="text-xs text-slate-400">
              A strong answer covers about {question.keyPointCount} points.
            </span>
          )}
        </div>
      </Card>

      {reveal && (
        <>
          <Card className={cx('border p-6', presentation.tone)}>
            <p className="label-mono mb-2 flex items-center gap-1.5 text-brand-teal-dark">
              <SourceIcon size={13} weight="bold" /> {presentation.label}
            </p>
            {reveal.answerEnhanced || reveal.answer ? (
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                {reveal.answerEnhanced || reveal.answer}
              </p>
            ) : (
              <p className="text-sm text-slate-500">
                This one has no model answer on purpose — scenario questions rarely have a single right answer. Compare
                your response against the key points below.
              </p>
            )}
          </Card>

          {reveal.keyPoints.length > 0 && (
            <Card className="p-6">
              <p className="label-mono mb-2">What a strong answer covers</p>
              <ul className="space-y-1.5">
                {reveal.keyPoints.map((k, i) => (
                  <li key={i} className="flex gap-2 text-sm text-slate-700">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-teal" />
                    {k}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {gradingEnabled && reveal.judgement && (
            <Card className="p-6">
              <p className="label-mono mb-2">Feedback on your answer</p>
              <p className="font-display text-2xl font-bold text-brand-navy">
                {reveal.judgement.score}
                <span className="text-base font-medium text-slate-400">/100</span>
              </p>
              <p className="mt-1 text-sm font-medium text-slate-600">{reveal.judgement.verdict}</p>
              {reveal.judgement.gaps.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {reveal.judgement.gaps.map((g, i) => (
                    <li key={i} className="text-sm text-slate-600">— {g}</li>
                  ))}
                </ul>
              )}
              {reveal.judgement.feedback && <p className="mt-3 text-sm text-slate-700">{reveal.judgement.feedback}</p>}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
